/**
 * Event tracker: init – registers all trackers.
 * @package AdvancedTrackingManager
 */

import { onReady } from '@lib/dom';
import { sendEvent } from './core';
import {
  trackFormStart,
  trackFormSubmissions,
  trackSearch,
  trackViewSearchResults,
} from './forms';
import {
  trackDownloads,
  trackExternalLinks,
  trackEmailLinks,
  trackPhoneLinks,
  trackSocialLinks,
  trackSocialShare,
  trackCtaClick,
} from './links';
import {
  trackVideoNative,
  trackVideoIframes,
  trackAudioNative,
  observeDynamicVideoElements,
} from './media';
import { checkAndSendSlowPageLoad } from './page';

const w = globalThis as unknown as Window;

const run = (): void => {
  if (typeof w.atmConfig === 'undefined') return;

  const config = w.atmConfig;
  const searchParamName = (config.searchParamName?.trim() || 's');
  const downloadExtensions = config.downloadExtensions ?? [
    'pdf', 'xls', 'xlsx', 'doc', 'docx', 'txt', 'rtf', 'csv', 'exe', 'key',
    'pps', 'ppt', 'pptx', '7z', 'pkg', 'rar', 'gz', 'zip',
    'avi', 'mov', 'mp4', 'mpg', 'mpeg', 'wmv', 'midi', 'mid', 'mp3', 'wav', 'wma',
  ];

  const enable = {
    formStart: config.trackEventFormStart !== false,
    formSubmit: config.trackEventFormSubmit !== false,
    search: config.trackEventSearch !== false,
    viewSearchResults: config.trackEventViewSearchResults !== false,
    videoStart: config.trackEventVideoStart !== false,
    videoProgress: config.trackEventVideoProgress !== false,
    videoComplete: config.trackEventVideoComplete !== false,
    fileDownload: config.trackEventFileDownload !== false,
    externalLink: config.trackEventExternalLink !== false,
    emailLink: config.trackEventEmailLink !== false,
    phoneLink: config.trackEventPhoneLink !== false,
    slowPageLoad: config.trackEventSlowPageLoad !== false,
    ctaClick: config.trackEventCtaClick !== false,
  };
  if (config.trackEventFormSubmit === undefined && config.trackForms !== undefined) {
    enable.formSubmit = config.trackForms !== false;
  }
  if (config.trackEventFileDownload === undefined && config.trackDownloads !== undefined) {
    enable.fileDownload = config.trackDownloads !== false;
  }

  const sendPostTypeView = (): void => {
    const current = config.currentPostType;
    let tracked = config.trackedPostTypes;
    if (!current || tracked === undefined) return;
    if (!Array.isArray(tracked)) tracked = tracked ? Object.values(tracked) : [];
    if (!tracked.includes(current)) return;
    sendEvent(
      'post_type_view',
      {
        post_type: current,
        page_title: document.title || '',
        page_location: w.location?.href ?? w.location?.pathname ?? '',
      },
      config
    );
  };

  const CONSENT_READY_FALLBACK_MS = 5000;

  const init = (): void => {
    // Send page-level events only after consent is applied so GA accepts them (analytics_storage granted)
    let pageLevelSent = false;
    const maybeSendPageLevelEvents = (): void => {
      if (pageLevelSent) return;
      pageLevelSent = true;
      sendPostTypeView();
      if (config.is404 === true) {
        sendEvent(
          '404_view',
          {
            page_title: document.title || '',
            page_location: w.location?.href ?? w.location?.pathname ?? '',
          },
          config
        );
      }
      if (enable.slowPageLoad) {
        if (document.readyState === 'complete') {
          checkAndSendSlowPageLoad(config, enable);
        } else {
          w.addEventListener('load', () => checkAndSendSlowPageLoad(config, enable), { once: true });
        }
      }
    };
    if (w._atmConsentApplied === true) {
      maybeSendPageLevelEvents();
    } else {
      document.addEventListener('atm_consent_applied', maybeSendPageLevelEvents, { once: true });
      setTimeout(maybeSendPageLevelEvents, CONSENT_READY_FALLBACK_MS);
    }

    trackFormStart(config, enable);
    trackFormSubmissions(config, enable);
    trackSearch(config, enable, searchParamName);
    trackViewSearchResults(config, enable, searchParamName);
    trackDownloads(config, enable, downloadExtensions);
    trackExternalLinks(config, enable);
    trackEmailLinks(config, enable);
    trackPhoneLinks(config, enable);
    trackSocialLinks(config);
    trackSocialShare(config);
    trackCtaClick(config, enable);
    trackVideoNative(config, enable);
    trackAudioNative(config, enable);
    trackVideoIframes(config, enable);
    observeDynamicVideoElements(config, enable);
  };

  onReady(init);
};

run();
